import {
  Button,
  ClickAwayListener,
  Divider,
  FormControl,
  Paper,
  Popper,
  PopperProps,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import { useEffect, useMemo, useState } from 'react';
import { RowenaAvailableVariable, RowenaVariableFormat } from 'types/api/SystemTenant/RowenaAI/RowenaContentTypes';
import { FORMAT_LABELS, FORMAT_TOOLTIPS } from './rowenaFormat';

interface MentionFormatPopoverProps {
  anchorEl: PopperProps['anchorEl'];
  variable: RowenaAvailableVariable | undefined;
  variablesByName: Record<string, RowenaAvailableVariable>;
  initialFormat: string;
  initialControlledBy: string | null;
  initialOption: string | null;
  onSave: (changes: { format: RowenaVariableFormat; controlledBy: string | null; option: string | null }) => void;
  onClose: () => void;
}

const NONE_VALUE = '__none__';

export default function MentionFormatPopover({
  anchorEl,
  variable,
  variablesByName,
  initialFormat,
  initialControlledBy,
  initialOption,
  onSave,
  onClose,
}: MentionFormatPopoverProps) {
  const open = !!anchorEl && !!variable;

  const numericVariables = useMemo(
    () => Object.values(variablesByName).filter((v) => v.valueType === 'number'),
    [variablesByName]
  );

  const [draftFormat, setDraftFormat] = useState<string>(initialFormat);
  const [draftControlledBy, setDraftControlledBy] = useState<string | null>(initialControlledBy);
  const [draftOption, setDraftOption] = useState<string | null>(initialOption);

  // Reset draft when the popover switches to a different mention or when the
  // committed values on the underlying node change from the outside.
  useEffect(() => {
    setDraftFormat(initialFormat);
    setDraftControlledBy(initialControlledBy);
    setDraftOption(initialOption);
  }, [variable?.name, initialFormat, initialControlledBy, initialOption]);

  if (!open || !variable) return null;

  const supported = variable.supportedFormats ?? [];
  const showControlledBy = variable.valueType === 'string' && numericVariables.length > 0;
  const variableOptions = variable.options ?? [];
  const showOptions = variableOptions.length > 0;

  const isDirty =
    draftFormat !== initialFormat || draftControlledBy !== initialControlledBy || draftOption !== initialOption;

  const handleSave = () => {
    onSave({
      format: draftFormat as RowenaVariableFormat,
      controlledBy: draftControlledBy,
      option: draftOption,
    });
    onClose();
  };

  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      placement='bottom-start'
      modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
      popperOptions={{ strategy: 'fixed' }}
      style={{ zIndex: 1500, position: 'fixed' }}
    >
      <ClickAwayListener
        onClickAway={(event) => {
          const target = event.target as HTMLElement | null;
          if (target?.closest('[data-type="mention"]')) return;
          // Ignore clicks landing inside other MUI portals (Select menus,
          // tooltips, popovers) — they render outside the popper's DOM tree
          // but are conceptually part of this popover's interaction.
          if (target?.closest('.MuiPopover-root, .MuiModal-root, .MuiTooltip-popper')) return;
          onClose();
        }}
      >
        <Paper elevation={4} sx={{ p: 1.25, minWidth: 260, maxWidth: 380 }}>
          <Stack direction='row' alignItems='center' gap={0.5} sx={{ mb: 0.5 }}>
            <Typography variant='caption' sx={{ fontFamily: 'monospace', color: '#3B4797', fontWeight: 700 }}>
              @{variable.name}
            </Typography>
          </Stack>

          {showOptions && (
            <>
              <Typography variant='caption' sx={{ color: '#98A2AE', display: 'block', mb: 0.5 }}>
                Option
              </Typography>
              <FormControl size='small' fullWidth sx={{ mb: 1.25 }}>
                <Select
                  native
                  value={draftOption ?? NONE_VALUE}
                  onChange={(e) => {
                    const value = e.target.value as string;
                    setDraftOption(value === NONE_VALUE ? null : value);
                  }}
                  sx={{ fontSize: 12, fontFamily: 'monospace' }}
                >
                  <option value={NONE_VALUE}>(none)</option>
                  {variableOptions.map((opt) => (
                    <option key={opt.value} value={opt.value}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <Typography variant='caption' sx={{ color: '#98A2AE', display: 'block', mb: 1 }}>
            Display format
          </Typography>
          {supported.length === 0 ? (
            <Typography variant='caption' sx={{ color: '#98A2AE', fontStyle: 'italic' }}>
              No format options available for this variable.
            </Typography>
          ) : (
            <ToggleButtonGroup
              size='small'
              exclusive
              value={draftFormat}
              onChange={(_, value) => {
                if (value) setDraftFormat(value as string);
              }}
              sx={{
                flexWrap: 'wrap',
                gap: 0.5,
                '& .MuiToggleButton-root': {
                  border: '1px solid #E2E8F0',
                  borderRadius: 1,
                  textTransform: 'none',
                  py: 0.25,
                  px: 1,
                  fontSize: 12,
                  fontFamily: 'monospace',
                  color: '#3B4797',
                },
                '&& .MuiToggleButton-root.Mui-selected': {
                  backgroundColor: '#3B4797',
                  color: '#fff',
                  borderColor: '#3B4797',
                  fontWeight: 700,
                },
                '&& .MuiToggleButton-root.Mui-selected:hover': {
                  backgroundColor: '#2F3A7A',
                  borderColor: '#2F3A7A',
                },
                '& .MuiToggleButtonGroup-grouped:not(:first-of-type)': {
                  borderLeft: '1px solid #E2E8F0',
                  borderRadius: 1,
                  ml: 0,
                },
                '& .MuiToggleButtonGroup-grouped:not(:last-of-type)': {
                  borderRadius: 1,
                },
              }}
            >
              {supported.map((format) => (
                <ToggleButton key={format} value={format} title={FORMAT_TOOLTIPS[format] ?? ''}>
                  {FORMAT_LABELS[format] ?? format}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          )}

          {showControlledBy && (
            <>
              <Divider sx={{ my: 1.25 }} />
              <Typography variant='caption' sx={{ color: '#98A2AE', display: 'block', mb: 0.5 }}>
                Pluralize based on
              </Typography>
              <Typography variant='caption' sx={{ color: '#98A2AE', fontSize: 11, display: 'block', mb: 0.75 }}>
                Backend auto-pluralizes this word using the chosen counter's value.
              </Typography>
              <FormControl size='small' fullWidth>
                <Select
                  native
                  value={draftControlledBy ?? NONE_VALUE}
                  onChange={(e) => {
                    const value = e.target.value as string;
                    setDraftControlledBy(value === NONE_VALUE ? null : value);
                  }}
                  sx={{ fontSize: 12, fontFamily: 'monospace' }}
                >
                  <option value={NONE_VALUE}>(none)</option>
                  {numericVariables.map((v) => (
                    <option key={v.name} value={v.name}>
                      @{v.name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </>
          )}

          <Divider sx={{ my: 1.25 }} />
          <Stack direction='row' justifyContent='flex-end' gap={1}>
            <Button size='small' onClick={onClose} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              size='small'
              variant='contained'
              onClick={handleSave}
              disabled={!isDirty}
              sx={{ textTransform: 'none' }}
            >
              Save
            </Button>
          </Stack>
        </Paper>
      </ClickAwayListener>
    </Popper>
  );
}
